import * as React from 'react';
import { getTranslator, LanguageCode } from '../../Translator';
import { Link } from 'react-router-dom';
import { ElectionStatus, VoteResult } from '../../_generatedTypes.g';
import { getCandidateById, getPartyById, getElectionById } from '../../selectors';
import { RenderErrors } from '../ui/ErrorComponent';
import { OperationStatus } from '../../features/utils/operationSlice';
import { useAppSelector } from '../../hooks';

interface StatusTextProps {
    language: LanguageCode;
    voteResult: VoteResult;
    electionStatus: ElectionStatus;
    operationStatus: OperationStatus | null;
}

const renderStatusText = (props: StatusTextProps) => {
    const t = getTranslator(props.language);
    if (props.operationStatus != null) {
        return <div className={props.operationStatus.warning ? 'statusbanner--warning' : 'statusbanner--ok'}>
            <>{t[props.operationStatus.message]}</>
            {props.operationStatus.errors.length > 0 ? <RenderErrors errors={props.operationStatus.errors} /> : []}
        </div>;
    } else if (props.voteResult === VoteResult.Success) {
        return <div id="Election-Status-Voted" className="statusbanner--ok">{t['You voted in this election. Thank you.']}</div>;
    } else if (props.electionStatus === ElectionStatus.NotYetOpen) {
        return <div className="statusbanner--warning">{t['This election is not yet open.']}</div>;
    } else if (props.electionStatus === ElectionStatus.Closed) {
        return <div className="statusbanner--warning">{t['This election is already closed. Editing is not allowed.']}</div>;
    } else if (props.voteResult === VoteResult.NotYetVoted) {
        return null;
    } else if (props.voteResult === VoteResult.InProgress) {
        return <div className="statusbanner--ok">{t['Voting...']}</div>;
    } else if (props.voteResult === VoteResult.FailedElectionClosed) {
        return <div className="statusbanner--warning">{t['Voting failed, the election has closed.']}</div>;
    } else if (props.voteResult === VoteResult.FailedAlreadyVoted) {
        return <div className="statusbanner--warning">{t['Voting failed, you already voted in this election.']}</div>;
    } else {
        return <div className="statusbanner--warning">{t['Voting failed; reload the page and try again.']}</div>;
    }
};

interface NavigationProps {
    omitPartyInBreadCrumb: boolean;
    homeText: string;
    electionId: number;
    electionName: string;
    partyId?: number;
    partyName?: string;
    candidateId?: number;
    candidateName?: string;
}

const renderNavigation = (props: NavigationProps) =>
    <div className="electioncontainer__nav">
        <span className="electioncontainer__nav__link">
            <Link to="/">{props.homeText}</Link>
        </span>
        <span className="electioncontainer__nav__link">
            <Link to={`/election/${props.electionId}${props.omitPartyInBreadCrumb ? '/candidates' : ''}`}>{props.electionName}</Link>
        </span>
        {props.partyId && !props.omitPartyInBreadCrumb
            ? <span className="electioncontainer__nav__link">
                <Link to={`/party/${props.partyId}`}>{props.partyName}</Link>
            </span>
            : null}
        {props.candidateId
            ? <span className="electioncontainer__nav__link">
                <Link to={`/candidate/${props.candidateId}`}>{props.candidateName}</Link>
            </span>
            : null}
    </div>

type ElectionContainerProps = {
    children?: React.ReactNode;
    electionId?: number;
    partyId?: number;
    candidateId?: number;
}


export const ElectionContainer = (props: ElectionContainerProps) => {
    const candidates = useAppSelector(state => state.candidates);
    const parties = useAppSelector(state => state.parties);
    const elections = useAppSelector(state => state.elections);
    const language = useAppSelector(state => state.language);
    const operationStatus = useAppSelector(state => state.operationStatus);
    const t = getTranslator(language);

    let { electionId, partyId, candidateId } = props;
    let omitPartyInBreadCrumb = false;
    let partyName: string | undefined;
    let candidateName: string | undefined;

    if (candidateId !== undefined) {
        const candidate = getCandidateById(candidates, candidateId);
        candidateName = candidate.Name;
        partyId = candidate.PartyId;
        if (partyId == -1) {
            electionId = candidate.ElectionId;
        }
    }


    if (partyId != -1 && partyId !== undefined) {
        const party = getPartyById(parties, partyId);
        partyName = t.translate(party.Name);
        electionId = party.ElectionId;
        omitPartyInBreadCrumb = false /* election.heeftkandidatenstelsel*/;
    }


    const election = getElectionById(elections, electionId!);

    if (election) {
        omitPartyInBreadCrumb = election.CandidateSystem;
    }

    const statusProps: StatusTextProps = {
        electionStatus: election.ElectionStatus,
        voteResult: election.VoteResult,
        language: language,
        operationStatus: operationStatus,
    };
    const navProps: NavigationProps = {
        omitPartyInBreadCrumb,
        homeText: t['Home'],
        electionId: election.ElectionId,
        electionName: t.translate(election.Name),
        partyId,
        partyName,
        candidateId,
        candidateName,
    };

    return <div>
        {renderNavigation(navProps)}
        {renderStatusText(statusProps)}
        {props.children}
    </div>
}
